// OrgTree 데이터 로드.
// 1순위: vite plugin `/api/tpt/gw/org` (mongo `tpt_gw_dept`/`tpt_gw_user` 실시간)
// 2순위: 정적 `/org-tree.json` (G/W apply 안 했을 때 fallback)

import type { OrgDept } from '@tpt/design';
import i18n from '../i18n';
import { useAuthStore } from '../stores/authStore';
import { getNameLang2 } from './prefs';

// hidden — 🚫 표시 제외 부서(관리자 설정) 코드 + 그 소속 인원 rcUsername (2026-08-25). 사용자용 트리는 이미 잘려 오고,
//   users 는 rcSpotlight 가 이름 검색 결과를 거르는 데 쓴다. all=true(관리자)면 트리는 안 잘리고 노드에 hidden:true 표식.
export type OrgHidden = { depts: string[]; users: string[] };
// empVisible — 🏢 인원 세부구분(empType)이 실렸는지(관리자·조직도 관리 담당자, 2026-10-07)
type OrgResult = { tree: OrgDept; source: 'gw' | 'static'; stats?: { depts: number; users: number; companies: number }; hidden?: OrgHidden; empVisible?: boolean };

// 🔎 조직도 출처 필터 (관리자 전용, 2026-08-13) — src('gw'|'manual')로 인원을 거르고 결과가 빈 부서는 숨긴다.
//   src 없는 데이터(정적 폴백·구버전)는 'gw' 취급. OrgPanel(모바일)·OrgChartPanel(데스크톱 우측) 공용.
export function filterOrgTreeBySrc(d: OrgDept, f: { gw: boolean; manual: boolean }): OrgDept | null {
	const users = (d.users || []).filter((u) => ((u as { src?: string }).src === 'manual' ? f.manual : f.gw));
	const children = (d.children || []).map((c) => filterOrgTreeBySrc(c, f)).filter((c): c is OrgDept => !!c);
	if (users.length === 0 && children.length === 0) return null;
	return { ...d, users, children };
}
// 🅰 겸직 제외(기본부서만) — jobDept 마커가 붙은 배치(겸직 소속)를 트리에서 뺀다 (2026-08-31 원격 #3).
//   화면 라벨은 뒤집어 "겸직 포함"(끈 상태 = 이 필터 적용)으로 보여준다 — components/common/OrgFilterBar.
//   OrgPanel(조직도 화면)·RightPanel(우측 조직도 탭) 공용 (2026-09-02 원격, 종전엔 OrgPanel 안에만 있었다).
export function filterOrgTreeMainDeptOnly(d: OrgDept): OrgDept {
	const users = (d.users || []).filter((u) => !(u as { jobDept?: boolean }).jobDept);
	const children = (d.children || []).map(filterOrgTreeMainDeptOnly);
	// 바뀐 사람이 없으면 원본 참조를 그대로 — 불필요한 리렌더 방지
	const same = users.length === (d.users || []).length && children.every((c, i) => c === (d.children || [])[i]);
	return same ? d : { ...d, users, children };
}
// 🏷 부서 선택지 평탄화 — 동명 부서(회사별 "IT팀", 팀 아래 같은 이름 파트)를 구분하는 라벨 (2026-08-25 사용자).
//   label = "부서명 (회사 · 상위부서)" — 회사 직속이면 "부서명 (회사)". name 은 표·배지 등 짧은 표시용으로 그대로 둔다.
//   OrgManagePanel(일괄수정)·MemberInfoModal(개별수정) 공용 — 부서를 고르는 새 화면은 이 헬퍼를 쓸 것(GUIDE 등재).
export type DeptOption = { code: string; name: string; label: string; company?: string; parent?: string };
export function flattenDeptOptions(root: OrgDept): DeptOption[] {
	const out: DeptOption[] = [];
	const walk = (n: OrgDept, company: string, parent: string) => {
		const isCompany = !n.id || n.id.startsWith('company:');
		const isDept = !!n.id && !isCompany && !n.id.startsWith('gw-');
		if (isDept) {
			const ctx = [company, parent].filter(Boolean).join(' · ');
			out.push({ code: n.id, name: n.name, label: ctx ? `${n.name} (${ctx})` : n.name, company: company || undefined, parent: parent || undefined });
		}
		for (const c of n.children || []) walk(c, isCompany ? (n.id ? n.name : company) : company, isDept ? n.name : parent);
	};
	walk(root, '', '');
	return out;
}

// 캐시는 lang 별 — 선호언어 바뀌면 다른 표시명을 받아야 하므로 키 분리.
//   ⏳ 60초 TTL (2026-08-26 사용자 "대화방 목록엔 online 인데 조직도엔 offline") — 트리에는 접속상태(status)가
//   함께 실려 오는데 무기한 캐시라 세션 내내 첫 응답에 갇혔다. 사이드바는 60초 폴링이라 둘이 어긋났다.
//   서버(core/orgcache)는 30초 캐시라 재요청 비용은 대부분 캐시 히트로 흡수된다.
const ORG_TREE_TTL_MS = 60_000;
const cacheByLang = new Map<string, { at: number; p: Promise<OrgResult> }>();

function curLang(): string { return String(i18n.language || 'ko'); }

export function loadOrgTree(): Promise<OrgDept> {
	return loadOrgTreeWithMeta().then((r) => r.tree);
}

// opts.all — 관리자 화면(조직연동 > 조직도) 전용: 표시 제외 부서를 자르지 않은 전체 트리(hidden 표식 포함). 서버가 admin 역할을 재확인한다.
export async function loadOrgTreeWithMeta(opts: { all?: boolean } = {}): Promise<OrgResult> {
	const lang = curLang();
	const lang2 = getNameLang2();   // 🌐 제2 이름 표기 언어 — 미등록('')이면 서버가 name2 를 담지 않는다 (2026-09-15)
	const all = opts.all === true;
	const cacheKey = `${lang}|${lang2}|${all ? 'all' : 'user'}`;
	const hit = cacheByLang.get(cacheKey);
	if (hit && Date.now() - hit.at < ORG_TREE_TTL_MS) return hit.p;
	const p = (async () => {
		// G/W live 먼저
		try {
			const { authToken, userId } = useAuthStore.getState();
				const orgH: Record<string, string> = authToken && userId ? { 'X-Auth-Token': authToken, 'X-User-Id': userId } : {};
				const r = await fetch(`/api/tpt/gw/org?lang=${encodeURIComponent(lang)}${lang2 ? `&lang2=${encodeURIComponent(lang2)}` : ''}${all ? '&all=1' : ''}`, { headers: orgH });
			// 🔒 준회원(guest) 차단 — 401/403 이면 정적 폴백을 타지 않고 즉시 막는다.
			//   (정적 /org-tree.json 폴백이 가드를 우회해 준회원에게 조직도가 노출되던 보안 구멍 차단)
			if (r.status === 401 || r.status === 403) {
				throw new Error(`org forbidden: ${r.status}`);
			}
			if (r.ok) {
				const j = await r.json();
				if (j.tree) return { tree: j.tree as OrgDept, source: 'gw' as const, stats: j.stats, hidden: j.hidden as OrgHidden | undefined, empVisible: j.empVisible === true };
			}
		} catch (e) {
			if (/forbidden: 40[13]/.test((e as Error).message)) throw e; // 권한 차단은 폴백 없이 전파
			/* 그 외(네트워크/5xx)는 정적 폴백 */
		}
		// static fallback (GW 데이터 미가용 시에만 — 권한 차단은 위에서 이미 throw)
		const r2 = await fetch('/org-tree.json');
		if (!r2.ok) throw new Error(`org-tree static fail: ${r2.status}`);
		return { tree: (await r2.json()) as OrgDept, source: 'static' as const };
	})();
	cacheByLang.set(cacheKey, { at: Date.now(), p });
	// ⚠️ 실패한 promise 는 캐시하지 않는다 — 한 번 실패하면 영구히 "불러오는 중"(회색)에 갇히던 버그(#1).
	try { return await p; } catch (e) { cacheByLang.delete(cacheKey); throw e; }
}

export function invalidateOrgTree(): void {
	cacheByLang.clear();
}

// 📣 조직도 변경 알림 — **다른 창까지** (2026-10-07 형 "부서 순서 저장했는데 우측 패널 조직도엔 반영 안 됨").
//   조직도·인원 관리는 별도 창(데탑 팝아웃·웹 새 탭)이라 'tpt:org-changed' 창 이벤트가 메인 창에 닿지 않았다.
//   같은 출처 창끼리는 localStorage 'storage' 이벤트가 전달되므로 값을 바꿔 알리고, 받은 창은 캐시를 비우고 같은 창 이벤트로 바꿔 쏜다.
const ORG_BUMP_KEY = 'tpt-org-changed-at';
export function broadcastOrgChanged(): void {
	invalidateOrgTree();
	try { window.dispatchEvent(new CustomEvent('tpt:org-changed')); } catch { /* */ }
	try { localStorage.setItem(ORG_BUMP_KEY, String(Date.now())); } catch { /* private mode */ }
}
if (typeof window !== 'undefined') {
	window.addEventListener('storage', (e) => {
		if (e.key !== ORG_BUMP_KEY) return;
		invalidateOrgTree();
		try { window.dispatchEvent(new CustomEvent('tpt:org-changed')); } catch { /* */ }
	});
}
